type CsvColumn = {
  key: string;
  label: string;
};

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeCsv(value: unknown): string {
  const normalized = value == null ? '' : String(value);
  if (!/[",\n]/.test(normalized)) return normalized;
  return `"${normalized.replace(/"/g, '""')}"`;
}

export function downloadCsv(filename: string, columns: CsvColumn[], rows: Array<Record<string, unknown>>): void {
  const header = columns.map((column) => escapeCsv(column.label)).join(',');
  const lines = rows.map((row) => columns.map((column) => escapeCsv(row[column.key])).join(','));
  const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function openPrintableHtml(options: {
  title: string;
  bodyHtml: string;
  landscape?: boolean;
  variant?: 'modern' | 'legacy';
}): void {
  const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=1200,height=900');
  if (!printWindow) {
    throw new Error('Unable to open print window. Please allow popups for this site.');
  }

  const html = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(options.title)}</title>
    <style>
      @page { size: ${options.landscape ? 'A4 landscape' : 'A4'}; margin: 12mm; }
      * { box-sizing: border-box; }
      body { margin: 0; padding: 24px; color: #111827; font: 12px/1.5 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      h1, h2, h3, p { margin: 0; }
      .doc-shell { display: flex; flex-direction: column; gap: 16px; }
      .doc-header { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; border-bottom: 2px solid #0f172a; padding-bottom: 12px; }
      .doc-header h1 { font-size: 22px; font-weight: 700; }
      .doc-header p { color: #475569; }
      .doc-badge { display: inline-flex; align-items: center; border: 1px solid #cbd5e1; border-radius: 999px; padding: 4px 10px; font-size: 11px; font-weight: 600; color: #334155; }
      .meta-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px 12px; }
      .meta-card { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 12px; background: #f8fafc; }
      .meta-card .label { display: block; font-size: 10px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #64748b; }
      .meta-card .value { display: block; margin-top: 4px; white-space: pre-wrap; word-break: break-word; }
      .section-title { font-size: 14px; font-weight: 700; color: #0f172a; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; vertical-align: top; }
      th { background: #e2e8f0; font-size: 11px; font-weight: 700; }
      .text-right { text-align: right; }
      .legacy-doc { display: flex; flex-direction: column; gap: 12px; }
      .legacy-title { text-align: center; border: 2px solid #111827; padding: 10px 12px; }
      .legacy-title h1 { font-size: 18px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
      .legacy-title p { margin-top: 4px; color: #374151; font-size: 11px; }
      .legacy-meta-table th, .legacy-meta-table td { padding: 5px 7px; }
      .legacy-meta-table th { width: 13%; background: #f3f4f6; text-transform: uppercase; font-size: 10px; letter-spacing: .04em; }
      .legacy-line-table th, .legacy-line-table td { padding: 5px 6px; font-size: 11px; }
      .legacy-note { min-height: 54px; border: 1px solid #cbd5e1; padding: 8px; white-space: pre-wrap; }
      .legacy-signoff { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-top: 8px; }
      .legacy-signoff > div { border-top: 1px solid #111827; padding-top: 6px; min-height: 32px; font-size: 11px; }
      .toolbar { display: flex; justify-content: center; margin-top: 20px; }
      .toolbar button { border: none; border-radius: 999px; background: #0f172a; color: #fff; padding: 10px 18px; font-size: 13px; cursor: pointer; }
      @media print {
        body { padding: 0; }
        .toolbar { display: none; }
      }
    </style>
  </head>
  <body>
    <div class="print-root ${options.variant === 'legacy' ? 'print-root-legacy' : 'print-root-modern'}">${options.bodyHtml}</div>
    <div class="toolbar"><button onclick="window.print()">Print / Save PDF</button></div>
  </body>
</html>`;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}